// @school-apps/core/srs/pacer — the STUDY pacer (M·1): the sibling of core/lib/pacing (which paces
// assignments) for CARDS. Alivia's dashboard greeted her with a red "757 due" — the whole deck, not
// today's job — and her documented feedback is that wall-sized counters demoralize (F·1, at 354). The
// fix is a daily FRESH-CARD target: how many never-seen cards to see today so the whole deck is touched
// at least once by exam-minus-7 (the ramp window), leaving the ramp to cycle weak cards from there.
//
// PURE: no I/O, no writes, clock injected — it is calendar arithmetic + a division over already-known
// counts, never wrong, only early (the lib/pacing precedent). Bundled CLIENT-side (subpath export, like
// srs/fsrs and lib/pacing) so M·2's dashboard tile renders straight from it. The store (paceCounts)
// supplies the two counts; this owns only the rules. It NEVER touches srs, never re-derives due-ness,
// never changes session size or queue membership.
//
// Fresh-only, by design: returning FSRS reviews ride along in sessions but NEVER count against or
// inflate the target — `reviewedTodayFresh` is FIRST-EVER reviews today, so a good study day (or a
// re-review) can't punish tomorrow. Recomputed live every call from real counts: skip days → the number
// rises; grind → it falls. No stored plan, no "behind" state, no streak-guilt.
import { upcomingExams, RAMP_WINDOW_DAYS } from "../exams/exams.js"; // reuse the exam pick + date math

/** The sustainable daily default — one bounded session (F·1's SESSION_SIZE). Used when there's no real
 * exam to pace toward (none / past / beyond the horizon). */
export const DEFAULT_TARGET = 20;

/** The hard ceiling on today's target — three bounded sessions. Never print an impossible number: when
 * the exam is inside the ramp window with a backlog left (runway ≤ 0), the ramp decides which cards
 * matter from here, so the target caps here rather than demanding the whole remaining deck in a day. */
export const CAP_TARGET = 60;

/** Beyond this much runway an exam is too far to be TODAY's pacing driver — fall back to the sustainable
 * default (~6 months; a full semester + buffer paces, a next-year exam does not). */
export const MAX_RUNWAY_DAYS = 180;

const int = (n) => (Number.isFinite(n) ? Math.max(0, Math.trunc(n)) : 0);

/**
 * The study pace for TODAY, recomputed live from real state. Pure.
 * @param {object} arg
 * @param {number} arg.unseenCount        live cards with NO review history (store's "never reviewed" notion)
 * @param {number} arg.reviewedTodayFresh FIRST-EVER reviews done today (re-reviews excluded — fresh only)
 * @param {object|Array|null} arg.exam    the exam(s) to pace toward; the nearest FUTURE one is picked via
 *                                        upcomingExams (a single exm_ record, an array, or null)
 * @param {string} arg.now                local date-only ISO (YYYY-MM-DD) OR an ISO timestamp — date only
 * @param {number} [arg.totalCards]       OPTIONAL (M·3, additive): total live cards. When given, it
 *                                        distinguishes a NEVER-STARTED deck (`totalCards === 0` → `empty`,
 *                                        an inviting "capture some notes" state) from an ALL-SEEN deck
 *                                        (`unseenCount === 0` with cards present → `maintenance`). Omit it
 *                                        (M·2/Alivia) and behavior is unchanged — a bare `unseen 0` stays
 *                                        `maintenance` (fine when the deck is known non-empty).
 * @returns {{ targetToday: number, doneToday: number, remainingToday: number,
 *             runwayDays: number|null, mode: "paced"|"default"|"capped"|"maintenance"|"empty" }}
 *   targetToday: fresh cards to aim for today · doneToday: fresh already seen today · remainingToday:
 *   max(0, target − done) · runwayDays: daysUntil(exam) − ramp window (null when no exam drives it) ·
 *   mode: WHY this number.
 */
export function studyPace({ unseenCount, reviewedTodayFresh, exam, now, totalCards } = {}) {
  const unseen = int(unseenCount);
  const doneToday = int(reviewedTodayFresh);
  const total = totalCards === undefined ? null : int(totalCards); // null = not provided (Alivia/M·2)
  const exams = Array.isArray(exam) ? exam : exam ? [exam] : [];
  const nearest = upcomingExams(exams, now)[0]; // reuse the pick + date math; past/invalid → undefined
  const runwayDays = nearest ? nearest.daysUntil - RAMP_WINDOW_DAYS : null;

  let raw, mode;
  if (total === 0) {
    raw = 0; mode = "empty"; // never STARTED — no cards at all; not "all seen", an inviting empty state
  } else if (unseen === 0) {
    raw = 0; mode = "maintenance"; // all cards seen — nothing fresh to pace; the ramp/normal queue carries on
  } else if (runwayDays === null || runwayDays > MAX_RUNWAY_DAYS) {
    raw = DEFAULT_TARGET; mode = "default"; // no real exam to pace toward — the sustainable session rate
  } else if (runwayDays <= 0) {
    raw = CAP_TARGET; mode = "capped"; // exam inside the ramp window, backlog left — cap, don't panic
  } else {
    raw = Math.min(Math.ceil(unseen / runwayDays), CAP_TARGET); mode = "paced"; // touch the deck by exam−7
  }
  const targetToday = Math.min(unseen, raw); // never ask for more fresh cards than exist
  return { targetToday, doneToday, remainingToday: Math.max(0, targetToday - doneToday), runwayDays, mode };
}
